import React, { useState, useMemo } from 'react';
import { AlertCircle, Calculator, ChevronDown, ChevronUp } from 'lucide-react';
import SportsbookLogo, { SPORTSBOOK_NAMES } from './SportsbookLogo';
import TeamLogo from './TeamLogo';

function formatPeriod(period, sportKey) {
  if (!period) return '';
  if (sportKey?.includes('basketball')) return `Q${period}`;
  if (sportKey?.includes('hockey')) return `P${period}`;
  if (sportKey?.includes('baseball')) {
    const sfx = ['st', 'nd', 'rd'][period - 1] || 'th';
    return `${period}${sfx} Inn`;
  }
  if (sportKey?.includes('football')) return `Q${period}`;
  if (sportKey?.includes('tennis')) return `Set ${period}`;
  return `P${period}`;
}

function americanToDecimal(odds) {
  if (odds > 0) return (odds / 100) + 1;
  return (100 / Math.abs(odds)) + 1;
}

function formatOdds(odds) {
  return odds > 0 ? `+${odds}` : `${odds}`;
}

// Dynamic rounding increment based on bet size to look natural
function getRoundingIncrement(amount) {
  if (amount >= 1000) return 10;  // $1000+ → round to $10
  if (amount >= 100) return 5;    // $100-999 → round to $5
  return 1;                       // < $100 → round to $1
}

// Round in specific direction with dynamic increment
function roundTo(amount, direction) {
  const increment = getRoundingIncrement(amount);
  return direction === 'floor'
    ? Math.floor(amount / increment) * increment
    : Math.ceil(amount / increment) * increment;
}

// Find best rounding combination for 2-way arb
function findBestRounded2Way(s1Raw, s2Raw, d1, d2) {
  const combos = [
    [roundTo(s1Raw, 'floor'), roundTo(s2Raw, 'floor')],
    [roundTo(s1Raw, 'floor'), roundTo(s2Raw, 'ceil')],
    [roundTo(s1Raw, 'ceil'),  roundTo(s2Raw, 'floor')],
    [roundTo(s1Raw, 'ceil'),  roundTo(s2Raw, 'ceil')],
  ];

  const results = combos.map(([s1, s2]) => {
    const total = s1 + s2;
    const profit = Math.min(s1 * d1, s2 * d2) - total;
    return { s1, s2, total, profit };
  });

  return results.filter(r => r.profit > 0).sort((a, b) => b.profit - a.profit)[0] || null;
}

// Find best rounding combination for 3-way arb
function findBestRounded3Way(s1Raw, s2Raw, sDrawRaw, d1, d2, dDraw) {
  const dirs = ['floor', 'ceil'];
  const combos = [];
  for (const dir1 of dirs) {
    for (const dir2 of dirs) {
      for (const dirD of dirs) {
        combos.push([roundTo(s1Raw, dir1), roundTo(s2Raw, dir2), roundTo(sDrawRaw, dirD)]);
      }
    }
  }

  const results = combos.map(([s1, s2, sDraw]) => {
    const total = s1 + s2 + sDraw;
    const profit = Math.min(s1 * d1, s2 * d2, sDraw * dDraw) - total;
    return { s1, s2, sDraw, total, profit };
  });

  return results.filter(r => r.profit > 0).sort((a, b) => b.profit - a.profit)[0] || null;
}

// Find minimum valid total bet using linear search
function findMinValidTotal(d1, d2, dDraw = null, startTotal = 50) {
  const totalImpl = (1/d1) + (1/d2) + (dDraw ? (1/dDraw) : 0);

  for (let T = startTotal; T <= 5000; T += 10) {
    const s1Raw = T * (1/d1) / totalImpl;
    const s2Raw = T * (1/d2) / totalImpl;
    const sDrawRaw = dDraw ? T * (1/dDraw) / totalImpl : 0;

    const best = dDraw
      ? findBestRounded3Way(s1Raw, s2Raw, sDrawRaw, d1, d2, dDraw)
      : findBestRounded2Way(s1Raw, s2Raw, d1, d2);

    if (best && best.profit > 0) return T;
  }
  return null;
}

// Find best rounding for side bet mode (1 fixed side, others need rounding)
function findBestRoundedSideBet(fixedStake, fixedDecimal, otherSides) {
  // Target payout from the fixed side
  const targetPayout = fixedStake * fixedDecimal;

  // Calculate raw stakes for other sides
  const rawStakes = otherSides.map(side => ({
    ...side,
    rawStake: targetPayout / side.decimal
  }));

  // Generate all floor/ceil combinations for other sides
  const dirs = ['floor', 'ceil'];
  let combos = [[]];
  for (let i = 0; i < rawStakes.length; i++) {
    const newCombos = [];
    for (const combo of combos) {
      for (const dir of dirs) {
        newCombos.push([...combo, roundTo(rawStakes[i].rawStake, dir)]);
      }
    }
    combos = newCombos;
  }

  const results = combos.map(stakes => {
    const otherResults = stakes.map((stake, i) => ({
      ...otherSides[i],
      stake
    }));
    const totalInvested = fixedStake + stakes.reduce((sum, s) => sum + s, 0);
    const payouts = [targetPayout, ...stakes.map((s, i) => s * otherSides[i].decimal)];
    const minPayout = Math.min(...payouts);
    const profit = minPayout - totalInvested;
    return { otherResults, totalInvested, profit };
  });

  return results.filter(r => r.profit > 0).sort((a, b) => b.profit - a.profit)[0] || null;
}

// Find minimum valid fixed side bet
function findMinValidSideBet(fixedDecimal, otherSides, startBet = 10) {
  for (let bet = startBet; bet <= 2000; bet += 5) {
    const best = findBestRoundedSideBet(bet, fixedDecimal, otherSides);
    if (best && best.profit > 0) return bet;
  }
  return null;
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

function ArbitrageCard({ opportunity, onViewGame, session }) {
  const [showCalc, setShowCalc] = useState(false);
  const [calcMode, setCalcMode] = useState('total'); // 'total' | 'side'
  const [totalBet, setTotalBet] = useState('');
  const [sideBet, setSideBet] = useState('');
  const [selectedSide, setSelectedSide] = useState('home');
  const [editOdds1, setEditOdds1] = useState('');
  const [editOdds2, setEditOdds2] = useState('');
  const [tracked, setTracked] = useState(false);

  const opp = opportunity;
  const isProp = opp.market === 'player_props';

  // Map fields based on arb type (game vs prop)
  const market = opp.market || 'h2h';
  const totalPoint = opp.home_point ?? opp.away_point ?? opp.point;
  const side1Label = isProp
    ? `Over ${opp.line}`
    : market === 'totals'
    ? `Over ${totalPoint ?? ''}`
    : market === 'spreads'
    ? opp.home_team
    : `${opp.home_team} ML`;
  const side1Odds = isProp ? opp.over_odds : opp.home_odds;
  const side1Book = isProp ? opp.over_sportsbook : opp.home_sportsbook;
  const side1BookKey = isProp ? opp.over_sportsbook_key : opp.home_sportsbook_key;

  const side2Label = isProp
    ? `Under ${opp.line}`
    : market === 'totals'
    ? `Under ${totalPoint ?? ''}`
    : market === 'spreads'
    ? opp.away_team
    : `${opp.away_team} ML`;
  const side2Odds = isProp ? opp.under_odds : opp.away_odds;
  const side2Book = isProp ? opp.under_sportsbook : opp.away_sportsbook;
  const side2BookKey = isProp ? opp.under_sportsbook_key : opp.away_sportsbook_key;

  const is3Way = !isProp && opp.has_draw && opp.draw_odds;

  const parsedEdit1 = parseInt(editOdds1);
  const parsedEdit2 = parseInt(editOdds2);
  const activeOdds1 = !isNaN(parsedEdit1) && parsedEdit1 !== 0 ? parsedEdit1 : side1Odds;
  const activeOdds2 = !isNaN(parsedEdit2) && parsedEdit2 !== 0 ? parsedEdit2 : side2Odds;

  const d1 = americanToDecimal(activeOdds1);
  const d2 = americanToDecimal(activeOdds2);
  const dDrawMid = is3Way ? americanToDecimal(opp.draw_odds) : 0;
  const computedTotalImpl = 1/d1 + 1/d2 + (dDrawMid ? 1/dDrawMid : 0);
  const computedProfitPct = computedTotalImpl < 1
    ? ((1 - computedTotalImpl) / computedTotalImpl * 100).toFixed(2)
    : null;

  const totalCalc = useMemo(() => {
    const T = parseFloat(totalBet);
    if (!T || T <= 0) return null;

    const dSide1 = americanToDecimal(activeOdds1);
    const dSide2 = americanToDecimal(activeOdds2);
    const dDraw = is3Way ? americanToDecimal(opp.draw_odds) : null;
    const totalImpl = (1 / dSide1) + (1 / dSide2) + (is3Way ? (1 / dDraw) : 0);

    const stakeSide1Raw = T * (1 / dSide1) / totalImpl;
    const stakeSide2Raw = T * (1 / dSide2) / totalImpl;
    const stakeDrawRaw = is3Way ? T * (1 / dDraw) / totalImpl : 0;

    // Use coordinated rounding to find best combination with positive profit
    const best = is3Way
      ? findBestRounded3Way(stakeSide1Raw, stakeSide2Raw, stakeDrawRaw, dSide1, dSide2, dDraw)
      : findBestRounded2Way(stakeSide1Raw, stakeSide2Raw, dSide1, dSide2);

    if (best) {
      // Valid rounding found with positive profit
      const actualTotal = best.total;
      const minPayout = best.profit + actualTotal;
      return {
        stakeSide1: best.s1.toFixed(0),
        stakeSide2: best.s2.toFixed(0),
        stakeDraw: is3Way ? best.sDraw.toFixed(0) : null,
        payout: minPayout.toFixed(2),
        profit: best.profit.toFixed(2),
        roi: ((best.profit / actualTotal) * 100).toFixed(2),
        minSuggested: null,
        profitSide1: (best.s1 * dSide1 - actualTotal).toFixed(2),
        profitSide2: (best.s2 * dSide2 - actualTotal).toFixed(2),
        profitDraw: is3Way ? (best.sDraw * dDraw - actualTotal).toFixed(2) : null,
      };
    }

    // No valid rounding found - find minimum valid total
    const minValid = findMinValidTotal(dSide1, dSide2, dDraw, Math.max(50, T));

    // Still show the best attempt (floor/floor) so user sees current situation
    const s1 = roundTo(stakeSide1Raw, 'floor');
    const s2 = roundTo(stakeSide2Raw, 'floor');
    const sDraw = is3Way ? roundTo(stakeDrawRaw, 'floor') : 0;
    const actualTotal = s1 + s2 + sDraw;
    const payoutSide1 = s1 * dSide1;
    const payoutSide2 = s2 * dSide2;
    const payoutDraw = is3Way ? sDraw * dDraw : Infinity;
    const minPayout = Math.min(payoutSide1, payoutSide2, payoutDraw);
    const profit = minPayout - actualTotal;

    return {
      stakeSide1: s1.toFixed(0),
      stakeSide2: s2.toFixed(0),
      stakeDraw: is3Way ? sDraw.toFixed(0) : null,
      payout: minPayout.toFixed(2),
      profit: profit.toFixed(2),
      roi: ((profit / actualTotal) * 100).toFixed(2),
      minSuggested: minValid,
      profitSide1: (s1 * dSide1 - actualTotal).toFixed(2),
      profitSide2: (s2 * dSide2 - actualTotal).toFixed(2),
      profitDraw: is3Way ? (sDraw * dDraw - actualTotal).toFixed(2) : null,
    };
  }, [totalBet, activeOdds1, activeOdds2, opp.draw_odds, is3Way]);

  const sideCalc = useMemo(() => {
    const X = parseFloat(sideBet);
    if (!X || X <= 0) return null;

    const dSide1 = americanToDecimal(activeOdds1);
    const dSide2 = americanToDecimal(activeOdds2);
    const dDraw = is3Way ? americanToDecimal(opp.draw_odds) : null;

    let selectedDecimal, otherSides;
    if (selectedSide === 'home') {
      selectedDecimal = dSide1;
      otherSides = is3Way
        ? [{ name: side2Label, decimal: dSide2, book: side2Book }, { name: 'Draw', decimal: dDraw, book: opp.draw_sportsbook || '—' }]
        : [{ name: side2Label, decimal: dSide2, book: side2Book }];
    } else if (selectedSide === 'away') {
      selectedDecimal = dSide2;
      otherSides = is3Way
        ? [{ name: side1Label, decimal: dSide1, book: side1Book }, { name: 'Draw', decimal: dDraw, book: opp.draw_sportsbook || '—' }]
        : [{ name: side1Label, decimal: dSide1, book: side1Book }];
    } else {
      // draw selected
      selectedDecimal = dDraw;
      otherSides = [
        { name: side1Label, decimal: dSide1, book: side1Book },
        { name: side2Label, decimal: dSide2, book: side2Book },
      ];
    }

    // Use coordinated rounding to find best combination with positive profit
    const best = findBestRoundedSideBet(X, selectedDecimal, otherSides);

    if (best) {
      // Valid rounding found with positive profit
      const profitSelected = X * selectedDecimal - best.totalInvested;
      return {
        selectedPayout: (X * selectedDecimal).toFixed(2),
        otherSides: best.otherResults.map(r => ({
          ...r,
          stake: r.stake.toFixed(0),
          profitIfWins: (r.stake * r.decimal - best.totalInvested).toFixed(2),
        })),
        totalInvested: best.totalInvested.toFixed(0),
        profit: best.profit.toFixed(2),
        roi: ((best.profit / best.totalInvested) * 100).toFixed(2),
        minSuggested: null,
        profitSelected: profitSelected.toFixed(2),
      };
    }

    // No valid rounding found - find minimum valid side bet
    const minValid = findMinValidSideBet(selectedDecimal, otherSides, Math.max(10, X));

    // Still show the best attempt (floor for all) so user sees current situation
    const targetPayout = X * selectedDecimal;
    const results = otherSides.map(side => ({
      ...side,
      stake: roundTo(targetPayout / side.decimal, 'floor').toFixed(0),
    }));

    const totalInvested = X + results.reduce((sum, r) => sum + parseFloat(r.stake), 0);
    const payouts = results.map(r => parseFloat(r.stake) * r.decimal);
    payouts.push(X * selectedDecimal);
    const minPayout = Math.min(...payouts);
    const profit = minPayout - totalInvested;
    const profitSelected = X * selectedDecimal - totalInvested;

    return {
      selectedPayout: (X * selectedDecimal).toFixed(2),
      otherSides: results.map(r => ({
        ...r,
        profitIfWins: (parseFloat(r.stake) * r.decimal - totalInvested).toFixed(2),
      })),
      totalInvested: totalInvested.toFixed(0),
      profit: profit.toFixed(2),
      roi: ((profit / totalInvested) * 100).toFixed(2),
      minSuggested: minValid,
      profitSelected: profitSelected.toFixed(2),
    };
  }, [sideBet, selectedSide, activeOdds1, activeOdds2, side1Label, side2Label, side1Book, side2Book, opp.draw_odds, opp.draw_sportsbook, is3Way]);

  async function handleTrack(stakeA, stakeB) {
    if (!session) return;
    const stripAnnotation = (name) => (name || '').replace(/\s*\([^)]+\)\s*$/, '').trim();
    const rawHome = opp.market === 'spreads' ? stripAnnotation(opp.home_team) : opp.home_team;
    const rawAway = opp.market === 'spreads' ? stripAnnotation(opp.away_team) : opp.away_team;
    const sideA = opp.market === 'totals' ? 'Over' : opp.market === 'spreads' ? rawHome : isProp ? 'Over' : opp.home_team;
    const sideB = opp.market === 'totals' ? 'Under' : opp.market === 'spreads' ? rawAway : isProp ? 'Under' : opp.away_team;
    const pointA = opp.market === 'spreads' ? opp.home_point : opp.market === 'totals' ? opp.point : null;
    const pointB = opp.market === 'spreads' ? (opp.away_point != null ? -opp.away_point : pointA) : opp.market === 'totals' ? opp.point : null;
    try {
      await fetch(`${API_BASE}/user/bets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          betType: 'arb',
          sportsbook: side1BookKey,
          eventName: opp.event,
          sportKey: opp.sport_key,
          market: opp.market,
          side: isProp ? `${opp.player} Over ${opp.line}` : opp.home_team,
          odds: activeOdds1,
          amount: parseFloat(stakeA) || 0,
          sportsbookB: side2BookKey,
          sideB: isProp ? `${opp.player} Under ${opp.line}` : opp.away_team,
          oddsB: activeOdds2,
          amountB: parseFloat(stakeB) || 0,
          notes: JSON.stringify({
            event_id: opp.event_id, sport_key: opp.sport_key, commence_time: opp.commence_time,
            home_team: rawHome, away_team: rawAway, side_a: sideA, side_b: sideB,
            point_a: pointA ?? null, point_b: pointB ?? null,
            ...(isProp ? { player_name: opp.player, prop_type: opp.prop_type, prop_line: opp.line } : {}),
          }),
        }),
      });
      setTracked(true);
    } catch (err) {
      console.error('Track failed:', err);
    }
  }

  return (
    <div className="opportunity-card arb-card">
      <div className="opp-header">
        <AlertCircle className="icon-blue" size={24} />
        <div>
          <h3
            className="clickable-event"
            onClick={() => onViewGame && onViewGame(opp.event_id)}
            title="View all odds for this game"
          >
            {isProp ? `${opp.player} - ${opp.prop_type}` : opp.event}
            {opp.market && opp.market !== 'h2h' && !isProp && (
              <span className="market-tag">{opp.market === 'spreads' ? 'Spread' : 'Total'}</span>
            )}
            {isProp && <span className="market-tag">Prop</span>}
          </h3>
          {isProp && <p className="prop-event-name">{opp.event}</p>}
          {opp.is_live ? (
            <p className="event-time event-time--live">● Live</p>
          ) : (
            <p className="event-time">{new Date(opp.commence_time).toLocaleString('en-US', {
              weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
            })}</p>
          )}
          {opp.is_live && opp.home_score != null && (
            <div className="mini-card-scoreboard">
              {(() => {
                const isTennis = opp.sport_key?.includes('tennis');
                const homeName = ((opp.actual_home_team || opp.home_team) || '').split(' ').pop();
                const awayName = ((opp.actual_away_team || opp.away_team) || '').split(' ').pop();
                const [homeGames, awayGames] = isTennis && opp.clock ? opp.clock.split('-').map(Number) : [null, null];
                return isTennis ? (
                  <table className="mini-score-tennis">
                    <thead>
                      <tr>
                        <th />
                        <th className="mini-score-col-label">Sets</th>
                        <th className="mini-score-col-label">Games</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="mini-score-name">{homeName}</td>
                        <td className="mini-score-val"><strong>{opp.home_score}</strong></td>
                        <td className="mini-score-val">{homeGames ?? '-'}</td>
                      </tr>
                      <tr>
                        <td className="mini-score-name">{awayName}</td>
                        <td className="mini-score-val"><strong>{opp.away_score ?? '-'}</strong></td>
                        <td className="mini-score-val">{awayGames ?? '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <div className="mini-score-teams">
                    <span>{homeName} <strong>{opp.home_score}</strong></span>
                    <span className="score-sep">·</span>
                    <span>{awayName} <strong>{opp.away_score ?? '-'}</strong></span>
                  </div>
                );
              })()}
              {opp.period != null && (
                <span className="mini-score-period">
                  {formatPeriod(opp.period, opp.sport_key)}{!opp.sport_key?.includes('tennis') && opp.clock ? ` ${opp.clock}` : ''}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="opp-details arb-details">
        <div className="arb-side">
          <h4>
            <TeamLogo teamName={(opp.home_team || '').replace(/\s*\([^)]+\)\s*$/, '').trim()} sportKey={opp.sport_key} size={16} />
            {side1Label}
          </h4>
          <div className="arb-side-book">
            <SportsbookLogo bookKey={side1BookKey} size={16} />
            <span className="arb-side-book-name">{SPORTSBOOK_NAMES[side1BookKey] || side1Book}</span>
          </div>
          <p className="odds">{formatOdds(side1Odds)}</p>
        </div>

        {is3Way && (
          <div className="arb-side">
            <h4>Draw</h4>
            <div className="arb-side-book">
              <SportsbookLogo bookKey={opp.draw_sportsbook_key} size={16} />
              <span className="arb-side-book-name">{SPORTSBOOK_NAMES[opp.draw_sportsbook_key] || opp.draw_sportsbook || '—'}</span>
            </div>
            <p className="odds">{formatOdds(opp.draw_odds)}</p>
          </div>
        )}

        <div className="arb-middle">
          <div className="profit-badge">
            {computedProfitPct ?? opp.profit_percentage}% Profit
          </div>
          <div className="total-prob">
            Total: {(computedTotalImpl * 100).toFixed(2)}%
          </div>
        </div>

        <div className="arb-side">
          <h4>
            <TeamLogo teamName={(opp.away_team || '').replace(/\s*\([^)]+\)\s*$/, '').trim()} sportKey={opp.sport_key} size={16} />
            {side2Label}
          </h4>
          <div className="arb-side-book">
            <SportsbookLogo bookKey={side2BookKey} size={16} />
            <span className="arb-side-book-name">{SPORTSBOOK_NAMES[side2BookKey] || side2Book}</span>
          </div>
          <p className="odds">{formatOdds(side2Odds)}</p>
        </div>
      </div>

      {/* Calculator Toggle */}
      <button className="arb-calc-toggle" onClick={() => setShowCalc(!showCalc)}>
        <Calculator size={16} />
        Bet Calculator
        {showCalc ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {/* Calculator Panel */}
      {showCalc && (
        <div className="arb-calculator">
          {/* Editable odds */}
          <div className="calc-odds-edit-row">
            <div className="calc-odds-edit-side">
              <span className="calc-odds-edit-label">{side1Label}</span>
              <input
                className="calc-odds-edit-input"
                type="number"
                value={editOdds1}
                onChange={e => setEditOdds1(e.target.value)}
                placeholder={String(side1Odds)}
              />
            </div>
            <div className="calc-odds-edit-side">
              <span className="calc-odds-edit-label">{side2Label}</span>
              <input
                className="calc-odds-edit-input"
                type="number"
                value={editOdds2}
                onChange={e => setEditOdds2(e.target.value)}
                placeholder={String(side2Odds)}
              />
            </div>
          </div>
          <div className="calc-mode-tabs">
            <button
              className={calcMode === 'total' ? 'active' : ''}
              onClick={() => setCalcMode('total')}
            >
              Total Budget
            </button>
            <button
              className={calcMode === 'side' ? 'active' : ''}
              onClick={() => setCalcMode('side')}
            >
              Fixed Side Bet
            </button>
          </div>

          {calcMode === 'total' ? (
            <div className="calc-body">
              <div className="calc-input-group">
                <label>Total amount to bet:</label>
                <div className="calc-input-wrap">
                  <span className="calc-currency">$</span>
                  <input
                    type="number"
                    placeholder="e.g. 1000"
                    value={totalBet}
                    onChange={(e) => setTotalBet(e.target.value)}
                    min="0"
                  />
                </div>
              </div>

              {totalCalc && (
                <div className="calc-results">
                  <div className="calc-row">
                    <span>Bet on {side1Label} ({side1Book}):</span>
                    <span className="calc-value">${totalCalc.stakeSide1}</span>
                  </div>
                  {totalCalc.stakeDraw && (
                    <div className="calc-row">
                      <span>Bet on Draw ({opp.draw_sportsbook || '—'}):</span>
                      <span className="calc-value">${totalCalc.stakeDraw}</span>
                    </div>
                  )}
                  <div className="calc-row">
                    <span>Bet on {side2Label} ({side2Book}):</span>
                    <span className="calc-value">${totalCalc.stakeSide2}</span>
                  </div>
                  <div className={`calc-row calc-profit ${parseFloat(totalCalc.profitSide1) <= 0 ? 'calc-negative' : ''}`}>
                    <span>If {side1Label} wins:</span>
                    <span className={`calc-value ${parseFloat(totalCalc.profitSide1) > 0 ? 'profit-value' : 'loss-value'}`}>
                      {parseFloat(totalCalc.profitSide1) >= 0 ? '+' : ''}${totalCalc.profitSide1}
                    </span>
                  </div>
                  {is3Way && totalCalc.profitDraw != null && (
                    <div className={`calc-row calc-profit ${parseFloat(totalCalc.profitDraw) <= 0 ? 'calc-negative' : ''}`}>
                      <span>If Draw:</span>
                      <span className={`calc-value ${parseFloat(totalCalc.profitDraw) > 0 ? 'profit-value' : 'loss-value'}`}>
                        {parseFloat(totalCalc.profitDraw) >= 0 ? '+' : ''}${totalCalc.profitDraw}
                      </span>
                    </div>
                  )}
                  <div className={`calc-row calc-profit ${parseFloat(totalCalc.profitSide2) <= 0 ? 'calc-negative' : ''}`}>
                    <span>If {side2Label} wins:</span>
                    <span className={`calc-value ${parseFloat(totalCalc.profitSide2) > 0 ? 'profit-value' : 'loss-value'}`}>
                      {parseFloat(totalCalc.profitSide2) >= 0 ? '+' : ''}${totalCalc.profitSide2}
                    </span>
                  </div>
                  {totalCalc.minSuggested && (
                    <div className="calc-row calc-warning">
                      <span>⚠️ Arb too thin at this amount</span>
                      <span className="calc-suggestion">Try ${totalCalc.minSuggested}+ total</span>
                    </div>
                  )}
                  {session && !totalCalc.minSuggested && (
                    <button
                      className={`calc-track-btn${tracked ? ' calc-track-btn--done' : ''}`}
                      onClick={() => !tracked && handleTrack(totalCalc.stakeSide1, totalCalc.stakeSide2)}
                      disabled={tracked}
                    >
                      {tracked ? 'Tracked ✓' : `Track Bet — $${totalCalc.stakeSide1} / $${totalCalc.stakeSide2}`}
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="calc-body">
              <div className="calc-input-group">
                <label>Pick a side:</label>
                <select value={selectedSide} onChange={(e) => setSelectedSide(e.target.value)}>
                  <option value="home">{side1Label} ({formatOdds(side1Odds)})</option>
                  {is3Way && <option value="draw">Draw ({formatOdds(opp.draw_odds)})</option>}
                  <option value="away">{side2Label} ({formatOdds(side2Odds)})</option>
                </select>
              </div>

              <div className="calc-input-group">
                <label>Amount on that side:</label>
                <div className="calc-input-wrap">
                  <span className="calc-currency">$</span>
                  <input
                    type="number"
                    placeholder="e.g. 500"
                    value={sideBet}
                    onChange={(e) => setSideBet(e.target.value)}
                    min="0"
                  />
                </div>
              </div>

              {sideCalc && (
                <div className="calc-results">
                  {sideCalc.otherSides.map((side, i) => (
                    <div key={i} className="calc-row">
                      <span>Bet on {side.name} ({side.book}):</span>
                      <span className="calc-value">${side.stake}</span>
                    </div>
                  ))}
                  <div className="calc-row">
                    <span>Total invested:</span>
                    <span className="calc-value">${sideCalc.totalInvested}</span>
                  </div>
                  <div className={`calc-row calc-profit ${parseFloat(sideCalc.profitSelected) <= 0 ? 'calc-negative' : ''}`}>
                    <span>If {selectedSide === 'home' ? side1Label : selectedSide === 'away' ? side2Label : 'Draw'} wins:</span>
                    <span className={`calc-value ${parseFloat(sideCalc.profitSelected) > 0 ? 'profit-value' : 'loss-value'}`}>
                      {parseFloat(sideCalc.profitSelected) >= 0 ? '+' : ''}${sideCalc.profitSelected}
                    </span>
                  </div>
                  {sideCalc.otherSides.map((side, i) => (
                    <div key={`pnl-${i}`} className={`calc-row calc-profit ${parseFloat(side.profitIfWins) <= 0 ? 'calc-negative' : ''}`}>
                      <span>If {side.name} wins:</span>
                      <span className={`calc-value ${parseFloat(side.profitIfWins) > 0 ? 'profit-value' : 'loss-value'}`}>
                        {parseFloat(side.profitIfWins) >= 0 ? '+' : ''}${side.profitIfWins}
                      </span>
                    </div>
                  ))}
                  {sideCalc.minSuggested && (
                    <div className="calc-row calc-warning">
                      <span>⚠️ Arb too thin at this amount</span>
                      <span className="calc-suggestion">Try ${sideCalc.minSuggested}+ on this side</span>
                    </div>
                  )}
                  {session && !sideCalc.minSuggested && (() => {
                    const stakeA = selectedSide === 'home' ? sideBet : sideCalc.otherSides.find(s => s.name === side1Label)?.stake;
                    const stakeB = selectedSide === 'away' ? sideBet : sideCalc.otherSides.find(s => s.name === side2Label)?.stake;
                    return (
                      <button
                        className={`calc-track-btn${tracked ? ' calc-track-btn--done' : ''}`}
                        onClick={() => !tracked && handleTrack(stakeA, stakeB)}
                        disabled={tracked}
                      >
                        {tracked ? 'Tracked ✓' : `Track Bet — $${stakeA} / $${stakeB}`}
                      </button>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ArbitrageCard;
